import { useCallback, useEffect, useState } from "react";

import {
  SegmentationSurveyCompletedV3,
  SegmentationSurveyStepCadCompleted,
  SegmentationSurveyStepFeaturesCompleted,
  SegmentationSurveyStepIndustryCompleted,
  SegmentationSurveyStepOrgSizeCompleted,
  SegmentationSurveyStepRoleCompleted,
  SegmentationSurveyStepUsageIntentCompleted,
  SegmentationSurveyStepWorkCompleted,
  SelectButton,
  SpinnerCenter,
  TextField,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";

import useNavigation from "../../hooks/navigation";
import { useSetAppProperties } from "../../hooks/useGetAppProperties";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import type { SegmentationSurveyEvents } from "../../lib/TrackingAdapter";
import Heading from "../Heading/Heading";
import MultistepForm from "../MultistepForm/MultistepForm";
import ProgressiveProfilingError from "../ProgressiveProfilingError/ProgressiveProfilingError";

import style from "./SegmentationSurvey.module.css";

type Industry = SegmentationSurveyStepIndustryCompleted["eventProperties"]["industry"];
type Work = SegmentationSurveyStepWorkCompleted["eventProperties"]["work"];
type OrgSize = SegmentationSurveyStepOrgSizeCompleted["eventProperties"]["org_size"];
type Features = SegmentationSurveyStepFeaturesCompleted["eventProperties"]["features"];
type UsageIntent = SegmentationSurveyStepUsageIntentCompleted["eventProperties"]["usage_intent"];
type Role = SegmentationSurveyStepRoleCompleted["eventProperties"]["user_job_role"];
type CadTools = SegmentationSurveyStepCadCompleted["eventProperties"]["cad_tools"];

const RANDOM_COUNT = 15;
const predefinedRandomOrder = Array.from(new Array(RANDOM_COUNT)).map(() =>
  Math.floor(Math.random() * RANDOM_COUNT * -1)
);
const mustSkipIndustryTypes: string[] = [
  "architecture",
  "digital_entertainment",
  "student_or_educator",
];

const DetailsField = ({ name }: { name: string }) => {
  const { t, m } = useTranslation();
  const { register } = useFormContext();
  return (
    <TextField
      autoFocus
      className={style.otherInput}
      onClick={(e) => e.stopPropagation()}
      placeholder={t(m.SegmentationSurvey.QuestionTwo.Other.SubTitle)}
      {...register(`${name.split(".")[0]}.details`)}
    />
  );
};

const useTrackFormTelemetry = () => {
  const tracker = useTracker();

  return (formData: Record<string, { value: string | string[]; details?: string }>) => {
    const events = Object.entries(formData)
      .map(([key, { value, details }]): SegmentationSurveyEvents | undefined => {
        if (value) {
          if (typeof value === "string") {
            tracker.trackSegmentationSurveyGAEvent(value);
          } else {
            value.forEach((feature) => {
              tracker.trackSegmentationSurveyGAEvent(feature);
            });
          }
          if (value !== "other" && !value.includes("other")) {
            details = undefined;
          }
          switch (key) {
            case "1": {
              return new SegmentationSurveyStepWorkCompleted({ work: value as Work, details });
            }
            case "2": {
              return new SegmentationSurveyStepIndustryCompleted({
                industry: value as Industry,
                details,
              });
            }
            case "3": {
              return new SegmentationSurveyStepCadCompleted({
                cadTools: value as CadTools,
                details,
              });
            }
            case "4": {
              return new SegmentationSurveyStepRoleCompleted({
                userJobRole: value as Role,
                details,
              });
            }
            case "5": {
              return new SegmentationSurveyStepUsageIntentCompleted({
                usageIntent: value as UsageIntent,
                details,
              });
            }
            case "6": {
              return new SegmentationSurveyStepFeaturesCompleted({
                features: value as Features,
                details,
              });
            }
            case "7": {
              return new SegmentationSurveyStepOrgSizeCompleted({ orgSize: value as OrgSize });
            }
          }
        }
      })
      .filter((event): event is SegmentationSurveyEvents => event !== undefined);
    events.push(new SegmentationSurveyCompletedV3());
    tracker.trackSegmentationSurveyEvents(events);
  };
};

const SegmentationSurvey = () => {
  const { t, m } = useTranslation();
  const navigation = useNavigation();

  const [currentStep, setCurrentStep] = useState(0);
  const [submitState, setSubmitState] = useState<"success" | "error" | "initial">("initial");

  const methods = useForm();
  const { handleSubmit, watch, control } = methods;
  const values = watch();

  const trackTelemetry = useTrackFormTelemetry();
  const { mutateAsync: mutateAppProperties } = useSetAppProperties();

  //eslint-disable-next-line
  const submitCallback = (formData: any) => {
    trackTelemetry(formData);

    const finishedAt = new Date().toISOString();
    const isStudentOrTeacher = formData[1].value === "student_or_educator";
    const isMAD = formData[1].value === "product_manufacturing";
    const isEnterprise = ["1001-10000", "10000+"].includes(formData[7].value);

    mutateAppProperties(
      {
        is_resegmentation_needed: false,
        is_mad: isMAD,
        survey_is_enterprise_potential: isEnterprise,
        segmentation_is_student_or_teacher: isStudentOrTeacher,
        segmentation_work_completed: true,
        segmentation_work_web_completed: true,
        segmentation_v2_finished_at: finishedAt,
        segmentation_v2_progressive_profiling_finished_at: finishedAt,
        segmentation_v2_download_intent: "personal",
        segmentation_v2_use_at_work: "me",
        segmentation_v2_company_design: "digital_art_and_media",
      },
      {
        onSuccess: () => {
          navigation.tryRedirectFromSearchParams();
          setSubmitState("success");
        },
      }
    );
  };

  useEffect(() => {
    const tabNavigation = (event: KeyboardEvent) => {
      if (event.key === "Tab" || event.key === "Enter") {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", tabNavigation);
    return () => {
      document.removeEventListener("keydown", tabNavigation);
    };
  }, []);

  const goToNext = useCallback(() => {
    if (values[1]?.value === "other") {
      handleSubmit(submitCallback)();
    } else {
      // Some timeout is needed to the select closing animation to finish, greatly reduces the number of flashes
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 100);
    }
  }, [currentStep, values, handleSubmit, submitCallback]);

  const goToPrevous = () => {
    setCurrentStep(currentStep - 1);
  };

  const retryCallback = () => {
    setSubmitState("initial");
  };

  return (
    <FormProvider {...methods}>
      <div className={style.container}>
        {submitState === "initial" && (
          <MultistepForm
            submitCallback={handleSubmit(submitCallback)}
            currentStep={currentStep}
            goToNextCallback={goToNext}
            goToPreviousCallback={goToPrevous}
            previousDisabled={false}
            nextDisabled={
              values[currentStep + 1]?.value === undefined ||
              values[currentStep + 1]?.value?.length === 0
            }
          >
            <MultistepForm.Step>
              <Heading type="h1">{t(m.SegmentationSurvey.General.Title)}</Heading>
              <Heading type="h2" className={style.label}>
                {t(m.SegmentationSurvey.QuestionOne.WhatKindOfWork)}
              </Heading>
              <Controller
                name="1.value"
                control={control}
                render={({ field: { value, onChange, name } }) => (
                  <SelectButton
                    className={style.radioGroup}
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                      const skip = mustSkipIndustryTypes.includes(value);
                      if (skip) {
                        handleSubmit(submitCallback)();
                      } else if (value && value !== "other") {
                        goToNext();
                      }
                    }}
                    type="radio"
                    size="small"
                  >
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[0] }}
                      title={t(m.SegmentationSurvey.QuestionOne.ProductManufacturing)}
                      value="product_manufacturing"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[1] }}
                      title={t(m.SegmentationSurvey.QuestionOne.Architecture)}
                      value="architecture"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[2] }}
                      title={t(m.SegmentationSurvey.QuestionOne.MediaAndEntertainment)}
                      value="digital_entertainment"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[3] }}
                      title={t(m.SegmentationSurvey.QuestionOne.EducationStudentTeacher)}
                      value="student_or_educator"
                    />
                    <SelectButton.Item
                      className={value?.includes("other") && style.otherButton}
                      size={value?.includes("other") ? "medium" : "small"}
                      title={t(m.SegmentationSurvey.QuestionFour.Other.Title)}
                      subtitle={value?.includes("other") && <DetailsField name={name} />}
                      value="other"
                    />
                  </SelectButton>
                )}
              />
            </MultistepForm.Step>

            <MultistepForm.Step>
              <Heading type="h2">{t(m.SegmentationSurvey.QuestionTwo.Title)}</Heading>
              <div className={style.label}>{t(m.SegmentationSurvey.QuestionTwo.SubTitle)}</div>
              <Controller
                name="2.value"
                control={control}
                render={({ field: { value, onChange, name } }) => (
                  <SelectButton
                    className={style.radioGroup}
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                      if (value && value !== "other") {
                        goToNext();
                      }
                    }}
                    type="radio"
                    size="medium"
                  >
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[0] }}
                      title={t(m.SegmentationSurvey.QuestionTwo.Transportation.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionTwo.Transportation.SubTitle)}
                      value="transportation"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[1] }}
                      title={t(m.SegmentationSurvey.QuestionTwo.IndustrialEquipment.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionTwo.IndustrialEquipment.SubTitle)}
                      value="industrial_equipment"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[2] }}
                      title={t(m.SegmentationSurvey.QuestionTwo.HighTech.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionTwo.HighTech.SubTitle)}
                      value="high_tech"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[3] }}
                      title={t(m.SegmentationSurvey.QuestionTwo.HomeAndLifestyle.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionTwo.HomeAndLifestyle.SubTitle)}
                      value="home_and_lifestyle"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[4] }}
                      title={t(m.SegmentationSurvey.QuestionTwo.FMCG.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionTwo.FMCG.SubTitle)}
                      value="fmcg"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[5] }}
                      title={t(m.SegmentationSurvey.QuestionTwo.ConstructionProducts.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionTwo.ConstructionProducts.SubTitle)}
                      value="construction_products"
                    />
                    <SelectButton.Item
                      className={value?.includes("other") && style.otherButton}
                      title={t(m.SegmentationSurvey.QuestionFour.Other.Title)}
                      subtitle={value?.includes("other") && <DetailsField name={name} />}
                      value="other"
                    />
                  </SelectButton>
                )}
              />
            </MultistepForm.Step>

            <MultistepForm.Step>
              <Heading type="h2">{t(m.SegmentationSurvey.QuestionThree.Title)}</Heading>
              <div className={style.label}>{t(m.SegmentationSurvey.QuestionThree.SubTitle)}</div>
              <Controller
                name="3.value"
                control={control}
                render={({ field: { value, onChange, name } }) => (
                  <SelectButton
                    className={style.radioGroup}
                    value={value}
                    onChange={onChange}
                    type="checkbox"
                    size="small"
                  >
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.None)}
                      value="none"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Alias)}
                      value="alias"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Autocad)}
                      value="autocad"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Catia)}
                      value="catia"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Creo)}
                      value="creo"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Fusion)}
                      value="fusion_360"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Inventor)}
                      value="inventor"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Onshape)}
                      value="onshape"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Shapr3D)}
                      value="shapr3d"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Sketchup)}
                      value="sketchup"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Siemens)}
                      value="siemens_nx"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.SolidEdge)}
                      value="solid_edge"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.SolidWorks)}
                      value="solidworks"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionThree.Rhino)}
                      value="rhino"
                    />
                    <SelectButton.Item
                      className={value?.includes("other") && style.otherButton}
                      size={value?.includes("other") ? "medium" : "small"}
                      title={t(m.SegmentationSurvey.QuestionFour.Other.Title)}
                      subtitle={value?.includes("other") && <DetailsField name={name} />}
                      value="other"
                    />
                  </SelectButton>
                )}
              />
            </MultistepForm.Step>
            <MultistepForm.Step>
              <Heading type="h2">{t(m.SegmentationSurvey.QuestionFour.Title)}</Heading>
              <div className={style.label}>{t(m.SegmentationSurvey.QuestionFour.SubTitle)}</div>
              <Controller
                name="4.value"
                control={control}
                render={({ field: { value, onChange, name } }) => (
                  <SelectButton
                    className={style.radioGroup}
                    value={value}
                    onChange={onChange}
                    type="checkbox"
                    size="medium"
                  >
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFour.Concepting.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFour.Concepting.SubTitle)}
                      value="concepting"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFour.Communication.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFour.Communication.SubTitle)}
                      value="communication"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFour.Prototyping.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFour.Prototyping.SubTitle)}
                      value="prototyping"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFour.Engineering.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFour.Engineering.SubTitle)}
                      value="engineering"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFour.Manufacturing.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFour.Manufacturing.SubTitle)}
                      value="manufacturing"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFour.Review.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFour.Review.SubTitle)}
                      value="review"
                    />
                    <SelectButton.Item
                      className={value?.includes("other") && style.otherButton}
                      title={t(m.SegmentationSurvey.QuestionFour.Other.Title)}
                      subtitle={value?.includes("other") && <DetailsField name={name} />}
                      value="other"
                    />
                  </SelectButton>
                )}
              />
            </MultistepForm.Step>
            <MultistepForm.Step>
              <Heading type="h2">{t(m.SegmentationSurvey.QuestionFive.Title)}</Heading>
              <div className={style.label}>{t(m.SegmentationSurvey.QuestionFive.SubTitle)}</div>
              <Controller
                name="5.value"
                control={control}
                render={({ field: { value, onChange, name } }) => (
                  <SelectButton
                    className={style.radioGroup}
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                      if (value && value !== "other") {
                        goToNext();
                      }
                    }}
                    type="radio"
                    size="medium"
                  >
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFive.Business.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFive.Business.SubTitle)}
                      value="professional"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFive.Personal.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFive.Personal.SubTitle)}
                      value="personal"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionFive.School.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionFive.School.SubTitle)}
                      value="edu"
                    />
                    <SelectButton.Item
                      className={value?.includes("other") && style.otherButton}
                      title={t(m.SegmentationSurvey.QuestionFour.Other.Title)}
                      subtitle={value?.includes("other") && <DetailsField name={name} />}
                      value="other"
                    />
                  </SelectButton>
                )}
              />
            </MultistepForm.Step>
            <MultistepForm.Step>
              <Heading type="h2">{t(m.SegmentationSurvey.QuestionSix.Title)}</Heading>
              <div className={style.label}>{t(m.SegmentationSurvey.QuestionSix.SubTitle)}</div>
              <Controller
                name="6.value"
                control={control}
                render={({ field: { value, onChange, name } }) => (
                  <SelectButton
                    className={style.radioGroup}
                    value={value}
                    onChange={onChange}
                    type="checkbox"
                    size="medium"
                  >
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[0] }}
                      title={t(m.SegmentationSurvey.QuestionSix.Communication.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionSix.Communication.SubTitle)}
                      value="communication"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[1] }}
                      title={t(m.SegmentationSurvey.QuestionSix.Iterating.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionSix.Iterating.SubTitle)}
                      value="iterating"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[2] }}
                      title={t(m.SegmentationSurvey.QuestionSix.Mobility.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionSix.Mobility.SubTitle)}
                      value="mobility"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[3] }}
                      title={t(m.SegmentationSurvey.QuestionSix.QuickOnboarding.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionSix.QuickOnboarding.SubTitle)}
                      value="quick_onboarding"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[4] }}
                      title={t(m.SegmentationSurvey.QuestionSix.Rendering.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionSix.Rendering.SubTitle)}
                      value="rendering"
                    />
                    <SelectButton.Item
                      style={{ order: predefinedRandomOrder[5] }}
                      title={t(m.SegmentationSurvey.QuestionSix.AR.Title)}
                      subtitle={t(m.SegmentationSurvey.QuestionSix.AR.SubTitle)}
                      value="ar"
                    />
                    <SelectButton.Item
                      className={value?.includes("other") && style.otherButton}
                      title={t(m.SegmentationSurvey.QuestionFour.Other.Title)}
                      subtitle={value?.includes("other") && <DetailsField name={name} />}
                      value="other"
                    />
                  </SelectButton>
                )}
              />
            </MultistepForm.Step>
            <MultistepForm.Step>
              <Heading type="h2">{t(m.SegmentationSurvey.QuestionSeven.Title)}</Heading>
              <div className={style.label}>{t(m.SegmentationSurvey.QuestionSeven.SubTitle)}</div>
              <Controller
                name="7.value"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SelectButton
                    className={classNames(style.radioGroup, style.columnLayout)}
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    type="radio"
                    size="small"
                  >
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionSeven.JustMe)}
                      value="me"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionSeven.First)}
                      value="2-50"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionSeven.Second)}
                      value="51-250"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionSeven.Third)}
                      value="251-1000"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionSeven.Fourth)}
                      value="1001-10000"
                    />
                    <SelectButton.Item
                      title={t(m.SegmentationSurvey.QuestionSeven.Fifth)}
                      value="10000+"
                    />
                  </SelectButton>
                )}
              />
            </MultistepForm.Step>
          </MultistepForm>
        )}

        {submitState === "success" && <SpinnerCenter />}
        {submitState === "error" && <ProgressiveProfilingError retryCallback={retryCallback} />}
      </div>
    </FormProvider>
  );
};

export default SegmentationSurvey;
